import useAgentHubCommission from 'agentHub/hooks/useAgentHubCommissionV2'
import FormatCurrency from 'components/Common/FormatCurrency'
import PriceRowCommission from 'LuxKit/PricePoints/PriceRowCommission'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import config from 'constants/config'
import { useAppSelector } from 'hooks/reduxHooks'
import { useMemo } from 'react'
import { FormattedMessage } from 'react-intl'

import UrgencyLabel from 'LuxKit/Label/UrgencyLabel'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import Group from 'components/utils/Group'

import AgentHubFlightSetCommissionLink from './AgentHubFlightSetCommissionLink'

interface Props {
  productType: App.ProductType
  kind?: 'default' | 'plain'
  offerId: string
  packageId?: string
  offerLocations?: Array<string>
  vendorName?: string
  offerTotal?: number
  /**
   * optional, should be used when there are multiple options inside offer
   * (hotel packages, tour variations, etc.) so that we can get a date range commission
   */
  optionId?: string
  offer?: App.AgentHubOfferPayload
  /** When provided, dates/rooms are used instead of URL params for commission fetching (e.g. tour accommodation). */
  checkIn?: string
  checkOut?: string
  rooms?: Array<App.Occupants>
  bedbankPackages?: Array<{ packageId: string; mx: string }>
}

function PriceRowAgentHubCommission(props: Props) {
  const {
    productType,
    kind = 'default',
    offerId,
    offerTotal = 0,
    packageId,
    optionId,
    offer,
    checkIn,
    checkOut,
    rooms,
    bedbankPackages,
  } = props
  const dateOverrides = useMemo(
    () => (checkIn && checkOut && rooms ? { checkIn, checkOut, rooms } : undefined),
    [checkIn, checkOut, rooms],
  )

  const customerView = useAppSelector(state => state.agentHub.customerView)
  const {
    commissionPercentage,
    packagesCommission,
    greatestPackageCommission,
    minBaseCommission,
    maxBaseCommission,
    dateRangesCommission,
    includesTaxesAndFees,
    commissionDisplay,
    incentiveCommissionPercentage,
    incentiveCommissionMaxAmount,
    includesMargin,
  } = useAgentHubCommission(offerId, offer, dateOverrides, bedbankPackages)
  const includesAllFees = includesTaxesAndFees && includesMargin
  const commissionLoading = useAppSelector(state => state.agentHub.offersCommissionLoading[offerId])
  const packageCommission = packageId ? packagesCommission.find(pkg => pkg.packageId === packageId) : null
  const dateRangeCommission = optionId ? dateRangesCommission[optionId] : null
  const isBedbankPackageCommission = productType === 'bedbank_hotel' && !!packageId

  const greatestCommissionWithDateRange = useMemo(() => {
    const greatestDateRangeCommission = Math.max(0, ...Object.values(dateRangesCommission).map(commission => commission.commissionPercentage))
    return greatestDateRangeCommission + commissionPercentage
  }, [dateRangesCommission, commissionPercentage])

  const commissionToDisplay = useMemo(() => {
    const dateRangeCommissionPercentage = dateRangeCommission?.commissionPercentage ?? 0
    if (packageCommission?.commissionPercentage) {
      return packageCommission.commissionPercentage + dateRangeCommissionPercentage
    }
    return commissionPercentage + dateRangeCommissionPercentage
  }, [dateRangeCommission, commissionPercentage, packageCommission])
  const shouldDisplayAsUpTo = commissionDisplay?.mode === 'upTo'
  const cappedIncentiveCommissionLabel = useMemo(() => <FormattedMessage
    id="E23HvAvABQtEddzmW923"
    defaultMessage="+ {percentage}% reward commission (up to <maxAmount></maxAmount>)"
    
    values={{
      percentage: incentiveCommissionPercentage,
      maxAmount: () => <FormatCurrency value={incentiveCommissionMaxAmount ?? 0} />,
    }}
  />, [incentiveCommissionPercentage, incentiveCommissionMaxAmount])
  const uncappedIncentiveCommissionLabel = useMemo(() => <FormattedMessage
    id="D_aTclXkYW7BN3yfw98D"
    defaultMessage="+ {percentage}% reward commission"
    
    values={{
      percentage: incentiveCommissionPercentage,
    }}
  />, [incentiveCommissionPercentage])
  const incentiveCommissionLabel = useMemo(() => {
    if (incentiveCommissionMaxAmount != null) return cappedIncentiveCommissionLabel
    return uncappedIncentiveCommissionLabel
  }, [cappedIncentiveCommissionLabel, incentiveCommissionMaxAmount, uncappedIncentiveCommissionLabel])

  const plainAmountLabel = useMemo(() => {
    const currency = () => <FormatCurrency value={offerTotal * (commissionToDisplay / 100)} />

    if (shouldDisplayAsUpTo && includesAllFees) {
      return <FormattedMessage
        id="coKS-iiMIUTgDMSRdt_m"
        defaultMessage="Earn up to <currency></currency> commission ({commissionPercentage}%)"
        
        values={{ commissionPercentage: commissionToDisplay, currency }}
      />
    }

    if (shouldDisplayAsUpTo && productType === 'cruise') {
      return <FormattedMessage
        id="ef9JZDbWY1eBhr9kXE2D"
        defaultMessage="Earn up to <currency></currency> commission ({commissionPercentage}%) on commissionable cruise fare"
        
        values={{ commissionPercentage: commissionToDisplay, currency }}
      />
    }

    if (shouldDisplayAsUpTo) {
      return <FormattedMessage
        id="kxwIRRp4iM-hbORaMfP_"
        defaultMessage="Earn up to <currency></currency> commission ({commissionPercentage}%) on commissionable fare"
        
        values={{ commissionPercentage: commissionToDisplay, currency }}
      />
    }

    if (includesAllFees) {
      return <FormattedMessage
        id="M1wTojO8Gv4EYWqux5vI"
        defaultMessage="Earn <currency></currency> commission ({commissionPercentage}%)"
        
        values={{ commissionPercentage: commissionToDisplay, currency }}
      />
    }

    if (productType === 'cruise') {
      return <FormattedMessage
        id="zaIE9IdGClB6CdGDwrCz"
        defaultMessage="Earn <currency></currency> commission ({commissionPercentage}%) on commissionable cruise fare"
        
        values={{ commissionPercentage: commissionToDisplay, currency }}
      />
    }

    return <FormattedMessage
      id="vKm1Ea9oOGUEHyvFZFdR"
      defaultMessage="Earn <currency></currency> commission ({commissionPercentage}%) on commissionable fare"
      
      values={{ commissionPercentage: commissionToDisplay, currency }}
    />
  }, [commissionToDisplay, includesAllFees, offerTotal, productType, shouldDisplayAsUpTo])

  const plainPercentageLabel = useMemo(() => {
    if (shouldDisplayAsUpTo && includesAllFees) {
      return <FormattedMessage
        id="Be8VHRG-3YBMdM6EqB2d"
        defaultMessage="Earn up to {commissionPercentage}% commission"
        
        values={{ commissionPercentage: commissionToDisplay }}
      />
    }

    if (shouldDisplayAsUpTo && productType === 'cruise') {
      return <FormattedMessage
        id="TEQZxkbRUXSDhShxvraI"
        defaultMessage="Earn up to {commissionPercentage}% commission on commissionable cruise fare"
        
        values={{ commissionPercentage: commissionToDisplay }}
      />
    }

    if (shouldDisplayAsUpTo) {
      return <FormattedMessage
        id="jSyqrMuZRNPvZIe9XZKm"
        defaultMessage="Earn up to {commissionPercentage}% commission on commissionable fare"
        
        values={{ commissionPercentage: commissionToDisplay }}
      />
    }

    if (includesAllFees) {
      return <FormattedMessage
        id="CmFBL0P2-B3C4UDUYUZu"
        defaultMessage="Earn {commissionPercentage}% commission"
        
        values={{ commissionPercentage: commissionToDisplay }}
      />
    }

    if (productType === 'cruise') {
      return <FormattedMessage
        id="ivVSmJkZ1YyLRQxjP9kK"
        defaultMessage="Earn {commissionPercentage}% commission on commissionable cruise fare"
        
        values={{ commissionPercentage: commissionToDisplay }}
      />
    }

    return <FormattedMessage
      id="aAfgi2_lIHpY-5r6aLjS"
      defaultMessage="Earn {commissionPercentage}% commission on commissionable fare"
      
      values={{ commissionPercentage: commissionToDisplay }}
    />
  }, [commissionToDisplay, includesAllFees, productType, shouldDisplayAsUpTo])

  if (config.agentHub.isEnabled && commissionLoading && !customerView) {
    return <TextLoadingBox typography="body-medium" width="8ch" />
  }

  // Bedbank package rows must not fall back to the offer-level commission
  if (isBedbankPackageCommission && !packageCommission) {
    return null
  }

  if (!config.agentHub.isEnabled || !productType || customerView) {
    return null
  }

  if (!commissionPercentage && !greatestPackageCommission) {
    if (productType === 'flight') {
      return <AgentHubFlightSetCommissionLink />
    }
    return null
  }

  if (kind === 'plain') {
    return <>
      {offerTotal > 0 && (
        <Group direction="vertical" gap={4}>
          <BodyTextBlock weight="bold" variant="small" colour="highlight-primary-dark">
            {plainAmountLabel}
          </BodyTextBlock>

          {incentiveCommissionPercentage > 0 && (
            <Group direction="horizontal" gap={4}>
              <UrgencyLabel icon={<SolidFireIcon />} variant="critical1">
                {incentiveCommissionLabel}
              </UrgencyLabel>
            </Group>
          )}
        </Group>
      )}
      {offerTotal === 0 && (
        <BodyTextBlock weight="bold" variant="small" colour="highlight-primary-dark">
          {plainPercentageLabel}
        </BodyTextBlock>
      )}
    </>
  }

  const multipleCommissions = shouldDisplayAsUpTo || greatestPackageCommission > commissionToDisplay || (Object.values(dateRangesCommission).some(dr => dr.commissionPercentage > 0) && !optionId)

  return <PriceRowCommission
    commissionPercentage={commissionToDisplay}
    productType={productType}
    offerTotal={offerTotal}
    multipleCommissions={multipleCommissions}
    greatestCommission={Math.max(greatestPackageCommission, greatestCommissionWithDateRange, commissionToDisplay)}
    minBaseCommission={minBaseCommission}
    maxBaseCommission={maxBaseCommission}
    includesTaxesAndFees={includesTaxesAndFees}
    includesMargin={includesMargin}
    incentivePercentage={incentiveCommissionPercentage}
  />
}

export default PriceRowAgentHubCommission
